import { useState, useCallback } from "react";
import { API_CORE } from "../../../../API/ApiCORE";
import { useUserAuthenticated } from "../../Contexts/_Auth/userAuthenticatedContext";
import { REACT_HOT_TOAST_ERROR, REACT_HOT_TOAST_SUCCESS } from "../../../../HELPERS/reactHotToast.helper";
import { isErrorStatusResponse, isSuccessStatusResponse, isWarningStatusResponse } from "../../../../API/_ApiUtil/statusResponseForMessages";

const METHOD_HANDLER = {
    POST: API_CORE.SERVICES.POST_RESOURCE,
    PUT: API_CORE.SERVICES.UPDATE_resource,
    DELETE: API_CORE.SERVICES.DELETE_resource,
    PATCH: API_CORE.SERVICES.PATCH_resource,
};

const METHODS_WITH_ID = new Set(["PUT", "DELETE", "PATCH"]);

export const useApiActionMutation = ({
    minTimeMutate = 2000,
    isToastError = false,
    isToastSuccess = false,
    // Por defecto, un 401 en una pantalla privada cierra la sesion.
    // Pantallas publicas de autenticacion (login) pueden desactivarlo para
    // mostrar el error sin enviar al usuario al Home.
    logoutOn401 = false,
} = {}) => {

    const { onCallbackCloseSessionUserAuth } = useUserAuthenticated();
    const [useActionApiIsLoading, setActionApiIsLoading] = useState(false);

    /*
        successMessage permite decir QUE paso, no solo que salio bien.
        El servidor devuelve un mensaje generico ("Operación exitosa"), que
        no ayuda: no es lo mismo crear una credencial que archivar un
        proyecto. Si la vista pasa su propio texto, ese manda.
    */
    const CB__useActionMutate = useCallback(async ({ method = "POST", endpoint = "", data = null, id = null, config = {}, successMessage = null }) => {

         const start_mutate = Date.now();

        setActionApiIsLoading(true);

        const keepMinTime = async () => {
            const elapsed = Date.now() - start_mutate;
            if (elapsed < minTimeMutate) {
                await new Promise(resolve => setTimeout(resolve, minTimeMutate - elapsed));
            }
        };

        const ACTION_HANDLER = METHOD_HANDLER[method];

        const RESOLVED_ENDPOINT = API_CORE.ENDPOINTS[endpoint];

        // METHOD
        if (!ACTION_HANDLER) {
            await keepMinTime();
            setActionApiIsLoading(false);
            return { code_status: 400, success: false, data: null, message: `Invalid method: ${method}` };
        }

        // ENDPOINT
        if (!RESOLVED_ENDPOINT) {
            await keepMinTime();
            setActionApiIsLoading(false);
            return { code_status: 400, success: false, data: null, message: `Invalid endpoint: ${endpoint}` };
        }

        let RESPONSE;

        try {
            if (METHODS_WITH_ID.has(method)) {
                RESPONSE = await ACTION_HANDLER(RESOLVED_ENDPOINT, id, data, config);
            } else {
                RESPONSE = await ACTION_HANDLER(RESOLVED_ENDPOINT, data, config);
            }
        } catch (error) {
            await keepMinTime();
            setActionApiIsLoading(false);
            const ERR_STATUS = error?.response?.status || 500;
            const ERR_MESSAGE = error?.response?.data?.message || error?.message || "Network error";
            if (isToastError) REACT_HOT_TOAST_ERROR(ERR_MESSAGE);
            if (ERR_STATUS === 401 && logoutOn401) onCallbackCloseSessionUserAuth();
            return { code_status: ERR_STATUS, success: false, data: null, message: ERR_MESSAGE };
        }

        await keepMinTime();
        setActionApiIsLoading(false);

        const CODE_STATUS = RESPONSE?.data?.status || RESPONSE?.status || RESPONSE?.data?.code || RESPONSE?.data?.statusCode || RESPONSE?.CODE_STATUS || (RESPONSE?.success === true ? 200 : 500);

        const DATA = RESPONSE?.data ?? RESPONSE?.data?.data ?? RESPONSE?.data?.payload ?? RESPONSE?.payload ?? null;

        const META = {
            verificationRequired: RESPONSE?.meta?.verificationRequired || RESPONSE?.data?.meta,
        };

        const RESPONSE_MESSAGE = RESPONSE?.data?.message || RESPONSE?.data?.msg || RESPONSE?.message || RESPONSE?.msg || RESPONSE?.data?.error || RESPONSE?.error || null;

        const SUCCESS = RESPONSE?.data?.success === true || RESPONSE?.success === true || RESPONSE?.data?.ok === true || RESPONSE?.ok === true || (CODE_STATUS >= 200 && CODE_STATUS < 300);

        const IS_ERROR_SERVER = isErrorStatusResponse(CODE_STATUS);
        const IS_ERROR_CLIENT = isWarningStatusResponse(CODE_STATUS);
        const IS_SUCCESS_STATUS = isSuccessStatusResponse(CODE_STATUS);

        // ----------------- ERROR 5XX (Servidor) -----------------
        if (IS_ERROR_SERVER) {
            if (isToastError && RESPONSE_MESSAGE) REACT_HOT_TOAST_ERROR(RESPONSE_MESSAGE);
            return { code_status: CODE_STATUS, success: false, data: DATA, message: RESPONSE_MESSAGE };
        }

        // ----------------- ERROR 4XX (Cliente) -----------------
        if (IS_ERROR_CLIENT) {
            if (isToastError && RESPONSE_MESSAGE) REACT_HOT_TOAST_ERROR(RESPONSE_MESSAGE);
            if (CODE_STATUS === 401 && logoutOn401) onCallbackCloseSessionUserAuth();
            return { code_status: CODE_STATUS, success: SUCCESS, data: DATA, message: RESPONSE_MESSAGE };
        }

        // ----------------- SUCCESS 2XX -----------------
        if (IS_SUCCESS_STATUS) {
            const MENSAJE_OK = successMessage ?? RESPONSE_MESSAGE;

            if (isToastSuccess && MENSAJE_OK) REACT_HOT_TOAST_SUCCESS(MENSAJE_OK);
            return { code_status: CODE_STATUS, success: SUCCESS, data: DATA, message: RESPONSE_MESSAGE, meta: META };
        }

        // ----------------- FALLBACK -----------------
        return { code_status: CODE_STATUS, success: SUCCESS, data: DATA, message: RESPONSE_MESSAGE || "Unexpected response" };

    }, [minTimeMutate, isToastError, isToastSuccess, logoutOn401, onCallbackCloseSessionUserAuth]);

    return { actionMutate: CB__useActionMutate, useActionApiIsLoading };
};